import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, PlusCircle, Search, MapPin, Eye, Trash2, Users, Building2, Loader2, AlertCircle } from 'lucide-react';
import { ServizioFiduciarioService } from '../services/apiService';
import { useToast } from '../components/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { UserRole, PreflightEliminazioneServizio } from '../types';
import ResponsiveTable from '../components/ResponsiveTable';
import ClientBadge from '../components/ClientBadge';
import { Modal } from '../components/Modal';

export const ServiziView: React.FC = () => {
    const navigate = useNavigate();
    const { addToast } = useToast();
    const { user, currentSedeId, sedi } = useAuth();

    // Stati per i dati reali
    const [servizi, setServizi] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Stati per i filtri
    const [filtro, setFiltro] = useState('');
    const [stato, setStato] = useState('');

    // Ricarica al cambio sede. Il backend filtra già per sedeId; il filtro
    // nel useMemo sotto è solo una sicurezza in più.
    useEffect(() => {
        const fetchServizi = async () => {
            setIsLoading(true);
            try {
                const sedeIdParam = typeof currentSedeId === 'number' ? currentSedeId : undefined;
                const res = await ServizioFiduciarioService.getAll(
                    sedeIdParam !== undefined ? { sedeId: sedeIdParam } : undefined
                );
                setServizi(res.data || res);
            } catch (error) {
                console.error("Errore fetch servizi:", error);
                addToast("Impossibile caricare i servizi fiduciari.", "error");
            } finally {
                setIsLoading(false);
            }
        };
        fetchServizi();
    }, [currentSedeId, addToast]);

    // Una sola modale con tre scelte esplicite, per evitare di eliminare il
    // servizio per errore quando si voleva solo annullare.
    const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
    const [deleting, setDeleting] = useState(false);
    const handleDelete = (id: number) => setDeleteTargetId(id);

    // Report o spese in attesa sui turni: il backend rifiuta l'eliminazione, quindi il
    // blocco si mostra prima della conferma.
    const [preflight, setPreflight] = useState<PreflightEliminazioneServizio | null>(null);
    const [preflightLoading, setPreflightLoading] = useState(false);
    useEffect(() => {
        if (deleteTargetId == null) { setPreflight(null); return; }
        let cancelled = false;
        (async () => {
            setPreflightLoading(true);
            try {
                const res = await ServizioFiduciarioService.preflightEliminazione(deleteTargetId);
                if (!cancelled) setPreflight(res);
            } catch (e) {
                // Preflight non disponibile: non blocchiamo, il backend rifiuta comunque con il suo messaggio.
                console.error('Errore preflight eliminazione servizio', e);
            } finally {
                if (!cancelled) setPreflightLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [deleteTargetId]);
    const bloccoInAttesa = preflight?.messaggioBlocco || null;
    const deleteBloccato = deleting || preflightLoading || !!bloccoInAttesa;

    const confirmDelete = async (cascade: boolean) => {
        const id = deleteTargetId;
        if (id == null || deleting) return;
        setDeleting(true);
        try {
            await ServizioFiduciarioService.delete(id, cascade);
            setServizi(prev => prev.filter(s => s.id !== id));
            addToast(
                cascade
                    ? "Servizio e preventivi/proforma collegati eliminati"
                    : "Servizio eliminato (preventivi/proforma scollegati)",
                "success",
            );
            setDeleteTargetId(null);
        } catch (error: any) {
            console.error("Errore eliminazione:", error);
            addToast(error?.response?.data?.message || "Errore durante l'eliminazione del servizio.", "error");
        } finally {
            setDeleting(false);
        }
    };

    // Filtro sede difensivo (il BE filtra già): ADMIN senza sede vede tutto,
    // ADMIN con sede solo quella, RESPONSABILE la sede scelta nel selettore.
    const showSedeColumn = user?.role === UserRole.ADMIN && currentSedeId === 'ALL';
    const filtered = useMemo(() => servizi.filter(s => {
        // Filtro sede per ruolo
        if (user?.role === UserRole.ADMIN) {
            if (currentSedeId !== 'ALL' && String(s.sedeId) !== String(currentSedeId)) return false;
        } else if (user?.role === UserRole.RESPONSABILE) {
            if (!s.sedeId || String(s.sedeId) !== String(currentSedeId ?? user.sedeId)) return false;
        }

        // Filtro testo + stato
        const clientName = s.clientName || '';
        const nomeServizio = s.nome || '';
        const matchText = nomeServizio.toLowerCase().includes(filtro.toLowerCase()) ||
            clientName.toLowerCase().includes(filtro.toLowerCase());
        const matchState = stato ? s.stato === stato : true;
        return matchText && matchState;
    }), [servizi, user, currentSedeId, filtro, stato]);

    const getSedeName = (id?: number) => {
        if (!id) return 'N/D';
        const sede = sedi.find(s => String(s.id) === String(id));
        return sede ? sede.name : `Sede #${id}`;
    };

    return (
        <div className="p-6 space-y-6 max-w-7xl mx-auto pb-24">
            {/* HEADER */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-600 text-white rounded-xl flex items-center justify-center shadow-lg shadow-emerald-200">
                        <Shield size={24} />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Servizi Fiduciari</h1>
                        <p className="text-sm text-gray-500">Gestisci portierato, sicurezza e turnazioni fisse.</p>
                    </div>
                </div>
                <button
                    onClick={() => navigate('/servizi/nuovo')}
                    className="bg-emerald-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-emerald-700 shadow-sm transition-all font-medium"
                >
                    <PlusCircle size={18} /> Nuovo Servizio
                </button>
            </div>

            {/* FILTRI */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                        type="text"
                        placeholder="Cerca per nome servizio o cliente..."
                        className="w-full pl-10 pr-4 py-2 border bg-gray-50 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none transition-shadow"
                        value={filtro}
                        onChange={e => setFiltro(e.target.value)}
                    />
                </div>
                <select
                    className="px-4 py-2 border bg-gray-50 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none cursor-pointer text-gray-700"
                    value={stato}
                    onChange={e => setStato(e.target.value)}
                >
                    <option value="">Tutti gli stati</option>
                    <option value="Attivo">Attivi</option>
                    <option value="Sospeso">Sospesi</option>
                    <option value="Terminato">Terminati</option>
                </select>
            </div>

            {/* TABELLA E STATI DI CARICAMENTO */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden min-h-[300px]">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center h-64 text-indigo-500 gap-3">
                        <Loader2 className="animate-spin" size={32} />
                        <p className="text-gray-500 font-medium">Caricamento servizi in corso...</p>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-16 text-gray-500">
                        <Shield className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                        <p className="text-lg font-medium text-gray-900">Nessun servizio trovato</p>
                        <p className="text-sm">Prova a cambiare i filtri o crea un nuovo servizio.</p>
                    </div>
                ) : (
                    <ResponsiveTable
                        data={filtered}
                        keyExtractor={(s) => s.id}
                        emptyMessage="Nessun servizio trovato"
                        columns={[
                            {
                                key: 'nome',
                                label: 'Servizio',
                                render: (s) => (
                                    <span className="inline-flex items-center gap-2">
                                        {/* Stesso #id citato in email e notifiche ("Servizio/Turno #id") */}
                                        <span className="font-mono text-xs text-gray-400 shrink-0">#{s.id}</span>
                                        <span
                                            className="font-bold text-blue-700 cursor-pointer hover:underline"
                                            onClick={() => navigate(`/servizi/${s.id}`)}
                                        >
                                            {s.nome}
                                        </span>
                                    </span>
                                ),
                            },
                            {
                                key: 'cliente',
                                label: 'Cliente',
                                render: (s) => (
                                    <span className="text-gray-700 inline-flex items-center gap-2">
                                        <Building2 size={16} className="text-gray-400"/>
                                        {s.clientName || <span className="text-gray-400 italic">Nessun Cliente</span>}
                                        <ClientBadge deleted={(s as any).clienteDeleted} anonymized={(s as any).clienteAnonymized} className="" />
                                    </span>
                                ),
                            },
                            {
                                key: 'indirizzo',
                                label: 'Postazione / Indirizzo',
                                hideOn: 'mobile',
                                render: (s) => (
                                    <div className="flex items-center gap-1 text-gray-500 text-sm">
                                        <MapPin className="w-4 h-4 text-red-400 shrink-0"/>
                                        <span className="truncate max-w-[200px]" title={s.indirizzo}>{s.indirizzo || '-'}</span>
                                    </div>
                                ),
                            },
                            ...(showSedeColumn ? [{
                                key: 'sede',
                                label: 'Sede',
                                render: (s: any) => (
                                    <span className="inline-flex items-center gap-1 text-orange-700 bg-orange-50 px-2 py-1 rounded border border-orange-100 font-medium text-xs">
                                        <Building2 size={12}/> {getSedeName(s.sedeId)}
                                    </span>
                                ),
                            }] : []),
                            {
                                key: 'operatori',
                                label: 'Operatori Attivi',
                                align: 'center',
                                render: (s) => (
                                    <div className="inline-flex items-center gap-1.5 bg-slate-100 px-2.5 py-1 rounded-full text-xs font-bold text-slate-700 border border-slate-200">
                                        <Users size={14}/> {s.tariffeCollaboratori?.length || 0}
                                    </div>
                                ),
                            },
                            {
                                key: 'stato',
                                label: 'Stato',
                                align: 'center',
                                render: (s) => (
                                    <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${
                                        s.stato === 'Attivo' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                            s.stato === 'Sospeso' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                                'bg-slate-100 text-slate-600 border-slate-200'
                                    }`}>
                                        {s.stato || 'Attivo'}
                                    </span>
                                ),
                            },
                            {
                                key: 'actions',
                                label: 'Azioni',
                                align: 'right',
                                render: (s) => (
                                    <div className="flex justify-end gap-2">
                                        <button
                                            onClick={() => navigate(`/servizi/${s.id}`)}
                                            className="p-2 text-indigo-600 hover:bg-indigo-100 rounded-lg transition-colors border border-transparent hover:border-indigo-200"
                                            title="Gestisci Servizio e Turni"
                                        >
                                            <Eye size={18}/>
                                        </button>
                                        <button
                                            onClick={() => handleDelete(s.id)}
                                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-200"
                                            title="Elimina"
                                        >
                                            <Trash2 size={18}/>
                                        </button>
                                    </div>
                                ),
                            },
                        ]}
                    />
                )}
            </div>

            {deleteTargetId != null && (
                <Modal title="Eliminare il servizio?" onClose={deleting ? undefined : () => setDeleteTargetId(null)}>
                    <p className="text-sm text-slate-700">
                        Il servizio e <b>tutti i suoi turni</b> verranno eliminati definitivamente.
                        Cosa vuoi fare con i preventivi/proforma collegati?
                    </p>
                    {preflightLoading && (
                        <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
                            <Loader2 className="animate-spin" size={16}/>
                            Verifica report e spese in attesa…
                        </div>
                    )}
                    {!preflightLoading && bloccoInAttesa && (
                        <div className="mt-4 bg-amber-50 border-2 border-amber-300 rounded-xl p-4">
                            <p className="text-sm font-bold text-amber-900 flex items-center gap-2">
                                <AlertCircle size={18}/>
                                Eliminazione non possibile
                            </p>
                            <p className="text-sm text-amber-900 mt-2 leading-relaxed">{bloccoInAttesa}</p>
                        </div>
                    )}
                    <div className="mt-5 flex flex-col gap-2">
                        <button
                            disabled={deleteBloccato}
                            onClick={() => confirmDelete(false)}
                            className="w-full px-4 py-2.5 text-sm font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-lg disabled:opacity-50"
                        >
                            Elimina servizio, scollega i preventivi
                            <span className="block text-[11px] font-normal opacity-90">restano in Preventivi come documenti a sé</span>
                        </button>
                        <button
                            disabled={deleteBloccato}
                            onClick={() => confirmDelete(true)}
                            className="w-full px-4 py-2.5 text-sm font-bold text-rose-700 bg-rose-50 border border-rose-300 hover:bg-rose-100 rounded-lg disabled:opacity-50"
                        >
                            Elimina servizio e anche i preventivi/proforma
                        </button>
                        <button
                            disabled={deleting}
                            onClick={() => setDeleteTargetId(null)}
                            className="w-full px-4 py-2.5 text-sm font-bold text-slate-700 bg-white border border-slate-300 hover:bg-slate-50 rounded-lg disabled:opacity-50"
                        >
                            {deleting ? <span className="inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Elimino…</span> : 'Annulla'}
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default ServiziView;